import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { CALC_CATEGORIES, getCategory } from "../utils/categories";
import type { CalcItem } from "../utils/acvRcv";

interface AddItemFormProps {
  onAdd: (item: CalcItem) => void;
}

const EMPTY = { name: "", categoryId: "other", rcv: "", age: "0", qty: "1" };

export default function AddItemForm({ onAdd }: AddItemFormProps) {
  const [draft, setDraft] = useState(EMPTY);
  const [sheetOpen, setSheetOpen] = useState(false);

  const cat = getCategory(draft.categoryId);

  const buildItem = (): CalcItem | null => {
    const rcvNum = Number(draft.rcv);
    if (!draft.name.trim() || !rcvNum || rcvNum <= 0) return null;
    return {
      id: `u_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      name: draft.name.trim(),
      categoryId: draft.categoryId,
      rcv: rcvNum,
      ageYears: Math.max(0, Number(draft.age) || 0),
      qty: Math.max(1, Number(draft.qty) || 1),
    };
  };

  const handleAdd = () => {
    const item = buildItem();
    if (!item) return;
    onAdd(item);
    setDraft(EMPTY);
    setSheetOpen(false);
  };

  const canAdd = draft.name.trim().length > 0 && Number(draft.rcv) > 0;

  return (
    <>
      {/* Mobile: single CTA that opens a sheet */}
      <Button
        type="button"
        variant="outline"
        onClick={() => setSheetOpen(true)}
        className="md:hidden w-full h-12 border-dashed text-base font-medium"
      >
        <Plus className="h-4 w-4 mr-2" /> Add your own item
      </Button>

      {/* Desktop: inline grid */}
      <div className="hidden md:block rounded-xl border border-dashed border-border bg-muted/30 p-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
          Add your own item
        </div>
        <div className="grid grid-cols-12 gap-2 items-start">
          <div className="col-span-3 flex flex-col">
            <label className="text-[11px] text-muted-foreground h-4">Name</label>
            <Input
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="e.g. Espresso machine"
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            />
          </div>
          <div className="col-span-3 flex flex-col">
            <label className="text-[11px] text-muted-foreground h-4">Category</label>
            <Select value={draft.categoryId} onValueChange={(v) => setDraft({ ...draft, categoryId: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {CALC_CATEGORIES.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-[10px] text-muted-foreground mt-1">
              Lifespan ~{cat.lifespan}y · {cat.curve}
            </div>
          </div>
          <div className="col-span-2 flex flex-col">
            <label className="text-[11px] text-muted-foreground h-4">Replace cost ($)</label>
            <Input
              type="number"
              inputMode="numeric"
              value={draft.rcv}
              onChange={(e) => setDraft({ ...draft, rcv: e.target.value })}
              placeholder="0"
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            />
          </div>
          <div className="col-span-1 flex flex-col">
            <label className="text-[11px] text-muted-foreground h-4">Age (yr)</label>
            <Input
              type="number"
              inputMode="numeric"
              value={draft.age}
              onChange={(e) => setDraft({ ...draft, age: e.target.value })}
            />
          </div>
          <div className="col-span-1 flex flex-col">
            <label className="text-[11px] text-muted-foreground h-4">Qty</label>
            <Input
              type="number"
              inputMode="numeric"
              value={draft.qty}
              onChange={(e) => setDraft({ ...draft, qty: e.target.value })}
              min={1}
            />
          </div>
          <div className="col-span-2 flex flex-col">
            <label className="text-[11px] text-muted-foreground h-4 sr-only md:not-sr-only">&nbsp;</label>
            <Button onClick={handleAdd} className="w-full" size="sm" disabled={!canAdd}>
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile add sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl max-h-[92vh] overflow-y-auto">
          <SheetHeader className="text-left">
            <SheetTitle className="font-editorial text-xl">Add an item</SheetTitle>
          </SheetHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-1.5">
              <Label htmlFor="add-name" className="text-xs text-muted-foreground">Name</Label>
              <Input
                id="add-name"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="e.g. Espresso machine"
                className="h-12 text-base"
                autoFocus
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="add-cat" className="text-xs text-muted-foreground">Category</Label>
              <Select value={draft.categoryId} onValueChange={(v) => setDraft({ ...draft, categoryId: v })}>
                <SelectTrigger id="add-cat" className="h-12 text-base">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-[60vh]">
                  {CALC_CATEGORIES.map((c) => (
                    <SelectItem key={c.id} value={c.id} className="py-3 text-base">
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[11px] text-muted-foreground">
                Lifespan ~{cat.lifespan}y · {cat.curve}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="add-rcv" className="text-xs text-muted-foreground">Replacement cost ($)</Label>
              <Input
                id="add-rcv"
                type="number"
                inputMode="decimal"
                value={draft.rcv}
                onChange={(e) => setDraft({ ...draft, rcv: e.target.value })}
                placeholder="0"
                className="h-12 text-base"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="add-age" className="text-xs text-muted-foreground">Age (years)</Label>
                <Input
                  id="add-age"
                  type="number"
                  inputMode="numeric"
                  value={draft.age}
                  onChange={(e) => setDraft({ ...draft, age: e.target.value })}
                  className="h-12 text-base"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="add-qty" className="text-xs text-muted-foreground">Quantity</Label>
                <Input
                  id="add-qty"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  value={draft.qty}
                  onChange={(e) => setDraft({ ...draft, qty: e.target.value })}
                  className="h-12 text-base"
                />
              </div>
            </div>
          </div>

          <SheetFooter className="flex-row gap-2 sm:flex-row">
            <Button
              variant="outline"
              onClick={() => { setDraft(EMPTY); setSheetOpen(false); }}
              className="flex-1 h-12"
            >
              Cancel
            </Button>
            <Button onClick={handleAdd} disabled={!canAdd} className="flex-1 h-12">
              <Plus className="h-4 w-4 mr-1" /> Add item
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}
